import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Claim Your Seat Upgrade!",
    description:
      "As a thank you for being a member, the Mets are pleased to offer you a complimentary seat upgrade for today's game. We look forward to welcoming you at Citi Field! You must have a valid game ticket to today's game in order to participate in this exclusive opportunity, and you may only claim the number of seats you have on your account. Upgrades claimed in connection with this program are for personal use only and may not be transferred or resold. Offer is online only, for a limited time only, subject to availability, and has no cash value. In the event a regulation game (as defined by MLB) is not played, upgrades claimed in connection with this program will be cancelled.",
    buttonLabel: "View Available Upgrades",
    authRedirectPage: routes.home,
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Why don't I see seat upgrades available for purchase?",
    answer:
      "You must have a valid game ticket to today's game in order to participate in this exclusive opportunity. Offer valid for select, non-premium seating areas.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability. Please check prior to your game to view available upgrades.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, your tickets will be available in the MLB Ballpark app.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections selected in your upgrade purchase. After successful purchase, Section/Row/Seat will be displayed on your new tickets available in the MLB Ballpark app.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Upgrades purchased in connection with this program are for personal use only and may not be transferred or resold.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "After successful purchase, your tickets cannot be changed. Upgrade purchases are non-refundable.",
  },
];

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from the New York Mets. You may unsubscribe at any time. By checking
    this box, you agree to receive additional commercial emails from the New
    York Mets and The Official Site of Major League Baseball. The terms and
    conditions of your ticket and policies applicable to its use are updated to
    address circumstances related to COVID-19 and health and safety. Further,
    due to the COVID-19 pandemic, scheduling may be impacted; games may be
    canceled, rescheduled, or postponed or games may be held with limited
    capacity. Upon completing your purchase by selecting &quot;Purchase
    Tickets&quot; you acknowledge and agree that your credit card will be
    charged. By clicking &quot;Purchase Tickets&quot;, you agree to the MLB
    Privacy Policy, Terms of Use and to receive a link to your ticket(s) via an
    SMS text message to the mobile # associated with your Amazin&apos; Mets Pass
    account. Message and data rates may apply. Tickets will be made available in
    the MLB Ballpark mobile application only. MLB and supported mobile device
    required.
  </>
);

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};
